import {
    Stack,
    Text,
    Loader,
    Title,
    Group as MGroup,
    ActionIcon,
} from "@mantine/core";
import slugify from "@sindresorhus/slugify";
import {modals} from "@mantine/modals";
import {notifications} from "@mantine/notifications";
import IconPin from "../../assets/pin.svg?react";
import IconPinFilled from "../../assets/pin-filled.svg?react";
import classes from "./DashboardContentPage.module.css";
import {useTranslation} from "react-i18next";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import type {TopicItemDto} from "../../dibAppApi/dibAppApi";
import {isToday, subDays} from "date-fns";
import {useMemo} from "react";
import {ExperimentalLink} from "../../components/ExperimentalLink";
import {getUrlForItem} from "../../utils/dibUrls";

type Group = {
    label: string;
    items: TopicItemDto[];
    showLabel: boolean;
    isPinned?: boolean;
};


export function DashboardContentPage() {
    const {data: workItems, isLoading} =
        dibAppApi.endpoints.getWorkItems.useQuery();
    const historyData = workItems?.history;
    const favoritesData = workItems?.favorites;
    const [patchFollow] = dibAppApi.endpoints.patchDocumentFollow.useMutation();
    const [deleteFollow] =
        dibAppApi.endpoints.deleteDocumentFollow.useMutation();
    const {t} = useTranslation();

    const handlePin = (item: TopicItemDto) => {
        if (!item.id) return;
        patchFollow({topicId: item.id})
            .unwrap()
            .catch(() => {
                notifications.show({
                    message: t("Error.SomethingWentWrong"),
                    color: "red",
                });
            });
    };

    const handleUnpin = (item: TopicItemDto) => {
        if (!item.id) return;
        const topicId = item.id;
        modals.openConfirmModal({
            title: t("Dashboard.unpin.confirm.title"),
            children: <Text size="lg">{item.name}</Text>,
            labels: {
                confirm: t("Word.Confirm"),
                cancel: t("common.cancel"),
            },
            confirmProps: {color: "var(--dib-brand-base)"},
            onConfirm: () => {
                deleteFollow({topicId})
                    .unwrap()
                    .catch(() => {
                        notifications.show({
                            message: t("Error.SomethingWentWrong"),
                            color: "red",
                        });
                    });
            },
        });
    };

    const groups = useMemo(() => {
        const result: Group[] = [];

        const pinnedItems = favoritesData?.items ?? [];
        if (pinnedItems.length > 0) {
            result.push({
                label: t("Widget.internal.portal.pinned"),
                items: pinnedItems,
                showLabel: true,
                isPinned: true,
            });
        }

        const items = [...(historyData?.items ?? [])];
        if (items.length === 0) return result;

        items.sort((a, b) => {
            const da = a.date ? new Date(a.date).getTime() : 0;
            const db = b.date ? new Date(b.date).getTime() : 0;
            return db - da;
        });

        const sevenDaysAgo = subDays(new Date(), 7);
        const today: TopicItemDto[] = [];
        const lastWeek: TopicItemDto[] = [];
        const previous: TopicItemDto[] = [];

        for (const item of items) {
            const date = item.date ? new Date(item.date) : null;
            if (date && isToday(date)) {
                today.push(item);
            } else if (date && date >= sevenDaysAgo) {
                lastWeek.push(item);
            } else {
                previous.push(item);
            }
        }

        const hasRecentGroup = today.length > 0 || lastWeek.length > 0;
        if (today.length > 0)
            result.push({
                label: t("date.today"),
                items: today,
                showLabel: true,
            });
        if (lastWeek.length > 0)
            result.push({
                label: t("date.lastWeek"),
                items: lastWeek,
                showLabel: true,
            });
        if (previous.length > 0)
            result.push({
                label: t("date.previously"),
                items: previous,
                showLabel: hasRecentGroup,
            });
        return result;
    }, [historyData?.items, favoritesData?.items, t]);

    if (isLoading) {
        return (
            <Stack align="center">
                <Loader />
            </Stack>
        );
    }

    if (groups.length === 0) {
        return (
            <Stack>
                <Text c="dimmed">{t("Widget.internal.portal.history.none")}</Text>
            </Stack>
        );
    }

    return (
        <Stack gap={0}>
            <Title order={3} mb="md" size={"lg"} className={classes.title}>
                {t("Widget.internal.portal.content")}
            </Title>
            {groups.map((group) => (
                <Stack key={group.label} gap={0} className={classes.groupStack}>
                    {group.showLabel && (
                        <Text className={classes.groupLabel}>
                            {group.label}
                        </Text>
                    )}
                    {group.items.map((item, i) => (
                        <MGroup
                            justify="space-between"
                            wrap="nowrap"
                            className={classes.itemRow}
                            key={`${group.label}-${item.id ?? i}-${item.date ?? ""}`}
                        >
                            <ExperimentalLink
                                {...getUrlForItem(item)}
                                className={classes.itemLink}
                            >
                                <Text className={classes.itemName}>
                                    {item.name}
                                </Text>
                                {item.description && (
                                    <Text className={classes.itemDescription}>
                                        {item.description}
                                    </Text>
                                )}
                            </ExperimentalLink>
                            {group.isPinned ? (
                                <ActionIcon
                                    variant="subtle"
                                    size="sm"
                                    aria-label={t("Dashboard.unpin")}
                                    disabled={!item.id}
                                    onClick={() => handleUnpin(item)}
                                >
                                    <IconPinFilled
                                        color="var(--dib-brand-base)"
                                        width={18}
                                        height={18}
                                    />
                                </ActionIcon>
                            ) : (
                                <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    size="sm"
                                    aria-label={t("Dashboard.pin")}
                                    disabled={!item.id}
                                    onClick={() => handlePin(item)}
                                >
                                    <IconPin width={18} height={18} />
                                </ActionIcon>
                            )}
                        </MGroup>
                    ))}
                </Stack>
            ))}
        </Stack>
    );
}
